import { useMemo, useState } from 'react'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import type {
  DateSelectArg,
  EventClickArg,
  EventContentArg,
  EventDropArg,
  EventInput,
} from '@fullcalendar/core'
import type { EventResizeDoneArg } from '@fullcalendar/interaction'
import {
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronDown,
  Cog,
  LayoutGrid,
  Plus,
  SlidersHorizontal,
  Trash2,
  User,
  X,
} from 'lucide-react'

import type {
  CgtFormType,
  ScheduledBlock,
  SchedulingResource,
  StepResourceAllocation,
  Procedure,
  ProcedureSchedule,
} from '@/types/cgt'
import { cn } from '@/lib/utils'
import { procedureSchedulingApi } from '@/api/procedureSchedulingApi'
import { schedulingKeys, useResources, useUpcomingProcedures } from '@/hooks/useProcedureSchedule'
import {
  annotateConflicts,
  todayISO,
  type BlockEdit,
  type BlockMove,
  type NewBlockInput,
} from '@/lib/scheduling'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ResourceCapacityGrid } from '@/components/scheduling/ResourceCapacityGrid'
import { blockColor, LEGEND } from '@/components/scheduling/blockColors'
import '@/components/scheduling/scheduling.css'

const START_HOUR = 8

function toEvent(block: ScheduledBlock): EventInput {
  const color = blockColor(block.formType)
  const startHour = String(START_HOUR).padStart(2, '0')
  const endHour = String(
    Math.min(23, START_HOUR + Math.max(1, block.durationHours)),
  ).padStart(2, '0')
  return {
    id: block.id,
    title: block.label,
    start: `${block.date}T${startHour}:00:00`,
    end: `${block.date}T${endHour}:00:00`,
    backgroundColor: color.hex,
    borderColor: color.hex,
    classNames: block.hasConflict ? ['sched-conflict'] : [],
    extendedProps: {
      procedureId: block.procedureId,
      hours: block.durationHours,
      conflict: block.hasConflict,
      resourceCount: block.allocations.length,
    },
  }
}

function renderEvent(arg: EventContentArg) {
  const { hours, conflict, resourceCount } = arg.event.extendedProps as {
    hours: number
    conflict: boolean
    resourceCount: number
  }
  const isList = arg.view.type === 'listWeek'
  return (
    <div className={cn('flex min-w-0 items-center gap-1 px-0.5', isList && 'text-foreground')}>
      {conflict && <AlertTriangle className="size-3 shrink-0" />}
      <span className="truncate font-semibold">{arg.event.title}</span>
      <span className="ml-auto shrink-0 whitespace-nowrap text-[10px] font-medium tabular-nums opacity-90">
        {hours}h · {resourceCount} res
      </span>
    </div>
  )
}

export function UpcomingProceduresPage() {
  const queryClient = useQueryClient()
  const { data: procedures = [], isLoading: loadingList } = useUpcomingProcedures()
  const { data: resources = [] } = useResources()
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)
  const [createDate, setCreateDate] = useState<string | null>(null)
  const [tab, setTab] = useState('grid')
  const [resourceFilter, setResourceFilter] = useState<Set<string>>(new Set())

  // One cached schedule query per upcoming procedure.
  const scheduleResults = useQueries({
    queries: procedures.map((t) => ({
      queryKey: schedulingKeys.schedule(t.id),
      queryFn: () => procedureSchedulingApi.getSchedule(t.id),
      enabled: Boolean(t.id),
    })),
  })

  // Reschedule / reassign a block — routed to whichever procedure owns it.
  const moveBlock = useMutation({
    mutationFn: ({
      procedureId,
      blockId,
      move,
    }: {
      procedureId: string
      blockId: string
      move: Omit<BlockMove, 'blockId'>
    }) => procedureSchedulingApi.updateBlock(procedureId, blockId, move),
    onSuccess: (schedule: ProcedureSchedule) => {
      queryClient.setQueryData(schedulingKeys.schedule(schedule.procedureId), schedule)
    },
  })

  // Full block edit (label / date / duration / resource allocations).
  const editBlock = useMutation({
    mutationFn: ({
      procedureId,
      blockId,
      patch,
    }: {
      procedureId: string
      blockId: string
      patch: Omit<BlockEdit, 'blockId'>
    }) => procedureSchedulingApi.editBlock(procedureId, blockId, patch),
    onSuccess: (schedule: ProcedureSchedule) => {
      queryClient.setQueryData(schedulingKeys.schedule(schedule.procedureId), schedule)
    },
  })

  // Create a new ad-hoc step on the clicked day, attached to the chosen procedure.
  const createBlock = useMutation({
    mutationFn: ({ procedureId, input }: { procedureId: string; input: NewBlockInput }) =>
      procedureSchedulingApi.createBlock(procedureId, input),
    onSuccess: (schedule: ProcedureSchedule) => {
      queryClient.setQueryData(schedulingKeys.schedule(schedule.procedureId), schedule)
      setCreateDate(null)
    },
  })

  // Delete a scheduled step.
  const deleteBlock = useMutation({
    mutationFn: ({ procedureId, blockId }: { procedureId: string; blockId: string }) =>
      procedureSchedulingApi.deleteBlock(procedureId, blockId),
    onSuccess: (schedule: ProcedureSchedule) => {
      queryClient.setQueryData(schedulingKeys.schedule(schedule.procedureId), schedule)
      setSelectedBlockId(null)
    },
  })

  // All scheduled steps across every upcoming procedure (flat — no procedure grouping).
  const procedureBlocks = useMemo<ScheduledBlock[]>(() => {
    const out: ScheduledBlock[] = []
    for (const result of scheduleResults) {
      if (!result.data) continue
      out.push(...result.data.blocks)
    }
    return out
  }, [scheduleResults])

  // Recompute conflicts across all steps — catches resources double-booked on
  // the same day (per-schedule data can't see cross-schedule contention).
  const { blocks: annotated, conflicts: aggConflicts } = useMemo(
    () => annotateConflicts(procedureBlocks, resources),
    [procedureBlocks, resources],
  )

  const resourceActive = (id: string) => resourceFilter.size === 0 || resourceFilter.has(id)

  // Resource filter: keep blocks touching at least one active resource.
  const displayBlocks = useMemo(
    () =>
      resourceFilter.size === 0
        ? annotated
        : annotated.filter((b) => b.allocations.some((a) => resourceActive(a.resourceId))),
    [annotated, resourceFilter],
  )

  const gridResources = useMemo(
    () => resources.filter((r) => resourceActive(r.id)),
    [resources, resourceFilter],
  )

  const earliestAnchor = useMemo(() => {
    const dates = annotated.map((b) => b.date).sort()
    return dates[0] ?? scheduleResults[0]?.data?.anchorDate
  }, [annotated, scheduleResults])

  // Merged schedule fed to the resource grid (it expects a single schedule shape).
  const mergedSchedule = useMemo<ProcedureSchedule>(
    () => ({
      procedureId: 'aggregate',
      patientMrn: '',
      procedureTemplateId: '',
      anchorDate: earliestAnchor ?? '',
      blocks: annotated,
      conflicts: aggConflicts,
    }),
    [annotated, aggConflicts, earliestAnchor],
  )

  const procedureIdByBlock = useMemo(() => {
    const map = new Map<string, string>()
    for (const b of annotated) map.set(b.id, b.procedureId)
    return map
  }, [annotated])

  const selected = displayBlocks.find((b) => b.id === selectedBlockId) ?? null
  const loadingSchedules = scheduleResults.some((r) => r.isLoading)
  const isLoading =
    loadingList || (procedures.length > 0 && loadingSchedules && annotated.length === 0)

  function move(blockId: string, m: Omit<BlockMove, 'blockId'>) {
    const procedureId = procedureIdByBlock.get(blockId)
    if (!procedureId) return
    moveBlock.mutate({ procedureId, blockId, move: m })
  }

  function handleEventDrop(info: EventDropArg) {
    const toDate = info.event.startStr.slice(0, 10)
    const procedureId = info.event.extendedProps.procedureId as string | undefined
    if (!toDate || !procedureId) {
      info.revert()
      return
    }
    moveBlock.mutate({ procedureId, blockId: info.event.id, move: { toDate } })
  }

  function handleEventResize(info: EventResizeDoneArg) {
    const { start, end } = info.event
    const procedureId = info.event.extendedProps.procedureId as string | undefined
    if (!start || !end || !procedureId) {
      info.revert()
      return
    }
    const durationHours = Math.max(1, Math.round((end.getTime() - start.getTime()) / 3_600_000))
    moveBlock.mutate({ procedureId, blockId: info.event.id, move: { durationHours } })
  }

  function handleEventClick(info: EventClickArg) {
    setCreateDate(null)
    setSelectedBlockId((cur) => (cur === info.event.id ? null : info.event.id))
  }

  // Click (or drag-select) an empty calendar slot → open the create drawer.
  function handleSelect(info: DateSelectArg) {
    setSelectedBlockId(null)
    setCreateDate(info.startStr.slice(0, 10))
    info.view.calendar.unselect()
  }

  function openCreate() {
    setSelectedBlockId(null)
    setTab('calendar')
    setCreateDate(earliestAnchor ?? todayISO())
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      {/* Toolbar */}
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {displayBlocks.length} steps
          {aggConflicts.length > 0 && (
            <span className="ml-1.5 inline-flex items-center gap-1 font-medium text-destructive">
              · <AlertTriangle className="size-3" /> {aggConflicts.length} conflict
              {aggConflicts.length > 1 ? 's' : ''}
            </span>
          )}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {/* Form-type legend */}
          <div className="hidden flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg border bg-muted/40 px-2.5 py-1 text-[11px] sm:flex">
            {LEGEND.map(({ formType, label }) => (
              <span key={formType} className="flex items-center gap-1 font-medium text-muted-foreground">
                <span className={cn('size-2 rounded-full', blockColor(formType).dot)} aria-hidden />
                {label}
              </span>
            ))}
          </div>

          {resources.length > 0 && (
            <ResourceFilter
              resources={resources}
              selected={resourceFilter}
              onChange={setResourceFilter}
            />
          )}

          {procedures.length > 0 && (
            <Button size="sm" className="h-8 gap-1.5" onClick={openCreate}>
              <Plus className="size-4" /> New step
            </Button>
          )}
        </div>
      </header>

      {isLoading ? (
        <div className="min-h-0 flex-1 animate-pulse rounded-xl bg-muted" />
      ) : annotated.length === 0 ? (
        <p className="flex min-h-0 flex-1 items-center justify-center rounded-xl border border-dashed text-center text-sm text-muted-foreground">
          {procedures.length === 0
            ? 'No upcoming procedures scheduled.'
            : 'No steps match the current filter.'}
        </p>
      ) : (
        <Tabs
          value={tab}
          onValueChange={setTab}
          className="flex min-h-0 flex-1 flex-col gap-3"
        >
          <TabsList className="shrink-0 self-start bg-muted/60">
            <TabsTrigger value="grid" className="gap-1.5">
              <LayoutGrid className="size-4" /> Resource grid
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-1.5">
              <CalendarDays className="size-4" /> Calendar
            </TabsTrigger>
          </TabsList>

          {/* Calendar tab */}
          <TabsContent
            value="calendar"
            className="mt-0 min-h-0 flex-1 flex-col gap-3 data-[state=active]:flex"
          >
            <div className="flex min-h-0 flex-1 gap-3">
              <div className="fc-shell min-h-0 flex-1 overflow-hidden rounded-xl border bg-card shadow-sm">
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                  initialView="timeGridWeek"
                  initialDate={earliestAnchor}
                  timeZone="UTC"
                  firstDay={1}
                  height="100%"
                  expandRows
                  stickyHeaderDates
                  handleWindowResize
                  nowIndicator
                  editable
                  selectable
                  selectMirror
                  eventStartEditable
                  eventDurationEditable
                  eventResizableFromStart
                  dayMaxEvents
                  weekNumbers
                  slotMinTime="06:00:00"
                  slotMaxTime="20:00:00"
                  allDaySlot={false}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                  }}
                  buttonText={{
                    today: 'Today',
                    month: 'Month',
                    week: 'Week',
                    day: 'Day',
                    list: 'Agenda',
                  }}
                  events={displayBlocks.map((b) => toEvent(b))}
                  eventContent={renderEvent}
                  eventDrop={handleEventDrop}
                  eventResize={handleEventResize}
                  eventClick={handleEventClick}
                  select={handleSelect}
                />
              </div>

              {selected ? (
                <BlockDrawer
                  key={selected.id}
                  mode="edit"
                  block={selected}
                  resources={resources}
                  saving={editBlock.isPending || deleteBlock.isPending}
                  onClose={() => setSelectedBlockId(null)}
                  onSave={(patch) =>
                    editBlock.mutate({
                      procedureId: selected.procedureId,
                      blockId: selected.id,
                      patch,
                    })
                  }
                  onDelete={() =>
                    deleteBlock.mutate({
                      procedureId: selected.procedureId,
                      blockId: selected.id,
                    })
                  }
                />
              ) : createDate ? (
                <BlockDrawer
                  key={`create-${createDate}`}
                  mode="create"
                  createDate={createDate}
                  procedures={procedures}
                  resources={resources}
                  saving={createBlock.isPending}
                  onClose={() => setCreateDate(null)}
                  onCreate={(procedureId, input) =>
                    createBlock.mutate({ procedureId, input })
                  }
                />
              ) : null}
            </div>
          </TabsContent>

          {/* Resource grid tab */}
          <TabsContent
            value="grid"
            className="mt-0 min-h-0 flex-1 flex-col data-[state=active]:flex"
          >
            <ResourceCapacityGrid
              schedule={mergedSchedule}
              resources={gridResources}
              fillHeight
              onMove={(blockId, m) => move(blockId, m)}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}

// ─── Resource filter dropdown ──────────────────────────────────────────────────

function ResourceFilter({
  resources,
  selected,
  onChange,
}: {
  resources: SchedulingResource[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
}) {
  const [open, setOpen] = useState(false)
  const equipment = resources.filter((r) => r.kind === 'equipment')
  const staff = resources.filter((r) => r.kind === 'staff')
  const groups: { label: string; icon: typeof Cog; rows: SchedulingResource[] }[] = [
    { label: 'Equipment', icon: Cog, rows: equipment },
    { label: 'Staff', icon: User, rows: staff },
  ].filter((g) => g.rows.length > 0)

  function toggle(id: string) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  const count = selected.size

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
          count > 0 ? 'border-primary/50 bg-primary/5 text-primary' : 'bg-card hover:bg-muted/50',
        )}
      >
        <SlidersHorizontal className="size-3.5" />
        Resources
        {count > 0 && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
            {count}
          </span>
        )}
        <ChevronDown className="size-3.5 opacity-60" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-40 mt-1.5 w-60 rounded-xl border bg-card p-2 shadow-lg">
            <div className="flex items-center justify-between px-1.5 pb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Filter by resource
              </span>
              {count > 0 && (
                <button
                  type="button"
                  className="text-[11px] font-medium text-primary hover:underline"
                  onClick={() => onChange(new Set())}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {groups.map((group) => (
                <div key={group.label}>
                  <div className="flex items-center gap-1.5 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                    <group.icon className="size-3" /> {group.label}
                  </div>
                  {group.rows.map((r) => {
                    const on = selected.has(r.id)
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => toggle(r.id)}
                        className="flex w-full items-center justify-between rounded-lg px-1.5 py-1.5 text-left text-xs hover:bg-muted/60"
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className={cn(
                              'flex size-4 items-center justify-center rounded border',
                              on ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                            )}
                          >
                            {on && <Check className="size-3" strokeWidth={3} />}
                          </span>
                          {r.name}
                        </span>
                        <span className="tabular-nums text-muted-foreground">
                          {r.capacityHoursPerDay}h
                        </span>
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Block detail drawer ───────────────────────────────────────────────────────

type BlockDrawerProps = {
  resources: SchedulingResource[]
  saving: boolean
  onClose: () => void
} & (
  | {
      mode: 'edit'
      block: ScheduledBlock
      onSave: (patch: Omit<BlockEdit, 'blockId'>) => void
      onDelete: () => void
    }
  | {
      mode: 'create'
      createDate: string
      procedures: Procedure[]
      onCreate: (procedureId: string, input: NewBlockInput) => void
    }
)

function BlockDrawer(props: BlockDrawerProps) {
  const { resources, saving, onClose } = props
  const isEdit = props.mode === 'edit'

  const [procedureId, setProcedureId] = useState(
    props.mode === 'create' ? (props.procedures[0]?.id ?? '') : props.block.procedureId,
  )
  const [formType, setFormType] = useState<CgtFormType | undefined>(
    props.mode === 'edit' ? props.block.formType : undefined,
  )
  const [label, setLabel] = useState(props.mode === 'edit' ? props.block.label : '')
  const [date, setDate] = useState(props.mode === 'edit' ? props.block.date : props.createDate)
  const [durationHours, setDurationHours] = useState(
    props.mode === 'edit' ? String(props.block.durationHours) : '4',
  )
  const [allocations, setAllocations] = useState<StepResourceAllocation[]>(
    props.mode === 'edit' ? props.block.allocations.map((a) => ({ ...a })) : [],
  )
  const [confirmDelete, setConfirmDelete] = useState(false)

  const color = blockColor(formType)
  const usedResourceIds = new Set(allocations.map((a) => a.resourceId))
  const available = resources.filter((r) => !usedResourceIds.has(r.id))

  const duration = Number(durationHours)
  const dirty =
    props.mode === 'edit'
      ? label !== props.block.label ||
        date !== props.block.date ||
        duration !== props.block.durationHours ||
        JSON.stringify(allocations) !== JSON.stringify(props.block.allocations)
      : true
  const valid =
    label.trim().length > 0 &&
    Boolean(date) &&
    Number.isFinite(duration) &&
    duration > 0 &&
    allocations.every((a) => a.resourceId && a.hours > 0) &&
    (isEdit || Boolean(procedureId))

  function setAllocation(index: number, patch: Partial<StepResourceAllocation>) {
    setAllocations((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)))
  }

  function addAllocation() {
    if (available.length === 0) return
    setAllocations((prev) => [...prev, { resourceId: available[0].id, hours: 1 }])
  }

  function removeAllocation(index: number) {
    setAllocations((prev) => prev.filter((_, i) => i !== index))
  }

  function handleSubmit() {
    if (!valid) return
    const cleanAllocations = allocations.map((a) => ({ ...a, hours: Number(a.hours) }))
    if (props.mode === 'edit') {
      if (!dirty) return
      props.onSave({
        label: label.trim(),
        date,
        durationHours: duration,
        allocations: cleanAllocations,
      })
    } else {
      props.onCreate(procedureId, {
        label: label.trim(),
        date,
        durationHours: duration,
        allocations: cleanAllocations,
        formType,
      })
    }
  }

  return (
    <aside className="relative flex w-80 shrink-0 flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
      <span className={cn('absolute inset-y-0 left-0 w-1', color.bar)} aria-hidden />

      <header className="flex items-start justify-between gap-2 border-b px-4 py-3 pl-5">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold">{isEdit ? 'Edit step' : 'New step'}</h4>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {isEdit ? props.block.label : 'Schedule a step on the calendar'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close details"
        >
          <X className="size-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3 pl-5">
        {props.mode === 'edit' && props.block.hasConflict && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-2.5 py-2">
            {props.block.conflictReasons.map((reason) => (
              <p key={reason} className="flex items-start gap-1.5 text-[11px] text-destructive">
                <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                {reason}
              </p>
            ))}
          </div>
        )}

        {props.mode === 'create' && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Transplant</Label>
              <Select value={procedureId} onValueChange={setProcedureId}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select transplant" />
                </SelectTrigger>
                <SelectContent>
                  {props.procedures.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.id} · {t.patientMrn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select
                value={formType ?? 'none'}
                onValueChange={(v) => setFormType(v === 'none' ? undefined : (v as CgtFormType))}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unspecified</SelectItem>
                  {LEGEND.map(({ formType: ft, label: ftLabel }) => (
                    <SelectItem key={ft} value={ft}>
                      {ftLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="blk-label" className="text-xs">
            Label
          </Label>
          <Input
            id="blk-label"
            value={label}
            placeholder={isEdit ? undefined : 'e.g. QC review'}
            onChange={(e) => setLabel(e.target.value)}
            className="h-8 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="blk-date" className="text-xs">
              Date
            </Label>
            <Input
              id="blk-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="blk-duration" className="text-xs">
              Duration (hrs)
            </Label>
            <Input
              id="blk-duration"
              type="number"
              min={1}
              value={durationHours}
              onChange={(e) => setDurationHours(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Resources</Label>
            <button
              type="button"
              onClick={addAllocation}
              disabled={available.length === 0}
              className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline"
            >
              <Plus className="size-3" /> Add
            </button>
          </div>

          {allocations.length === 0 && (
            <p className="rounded-lg border border-dashed px-2.5 py-2 text-[11px] text-muted-foreground">
              No resources assigned.
            </p>
          )}

          <div className="space-y-1.5">
            {allocations.map((a, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Select
                  value={a.resourceId}
                  onValueChange={(v) => setAllocation(i, { resourceId: v })}
                >
                  <SelectTrigger className="h-8 flex-1 text-xs">
                    <SelectValue placeholder="Resource" />
                  </SelectTrigger>
                  <SelectContent>
                    {resources.map((r) => (
                      <SelectItem
                        key={r.id}
                        value={r.id}
                        disabled={r.id !== a.resourceId && usedResourceIds.has(r.id)}
                      >
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={1}
                  value={String(a.hours)}
                  onChange={(e) => setAllocation(i, { hours: Number(e.target.value) })}
                  className="h-8 w-16 text-sm"
                  aria-label="Hours"
                />
                <button
                  type="button"
                  onClick={() => removeAllocation(i)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Remove resource"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="flex items-center justify-between gap-2 border-t px-4 py-3">
        {props.mode === 'edit' ? (
          confirmDelete ? (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">Delete?</span>
              <button
                type="button"
                onClick={props.onDelete}
                disabled={saving}
                className="font-semibold text-destructive hover:underline disabled:opacity-50"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="font-medium text-muted-foreground hover:underline"
              >
                No
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              className="flex items-center gap-1 text-[11px] font-medium text-destructive hover:underline disabled:opacity-50"
            >
              <Trash2 className="size-3.5" /> Delete
            </button>
          )
        ) : (
          <span />
        )}

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!dirty || !valid || saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create step'}
          </Button>
        </div>
      </footer>
    </aside>
  )
}
